import { Modal, Slider, Typography, message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  file: File | null;
  open: boolean;
  onCancel: () => void;
  onConfirm: (file: File) => Promise<void>;
};

type Point = { x: number; y: number };
type Size = { width: number; height: number };

const TARGET_BYTES = 256 * 1024;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('头像压缩失败')), 'image/webp', quality);
  });
}

export function AvatarCropModal({ file, open, onCancel, onConfirm }: Props) {
  const stageRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ pointerId: number; x: number; y: number; pan: Point } | null>(null);
  const [objectUrl, setObjectUrl] = useState('');
  const [natural, setNatural] = useState<Size>({ width: 1, height: 1 });
  const [stageSize, setStageSize] = useState(320);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!file) { setObjectUrl(''); return; }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!open || !stageRef.current) return;
    const updateSize = () => {
      const size = stageRef.current?.getBoundingClientRect().width || 320;
      setStageSize(size);
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, [open]);

  const geometry = useMemo(() => {
    const coverScale = Math.max(stageSize / natural.width, stageSize / natural.height);
    const scale = coverScale * zoom;
    const width = natural.width * scale;
    const height = natural.height * scale;
    const maxX = Math.max(0, (width - stageSize) / 2);
    const maxY = Math.max(0, (height - stageSize) / 2);
    const safePan = { x: clamp(pan.x, -maxX, maxX), y: clamp(pan.y, -maxY, maxY) };
    return {
      scale,
      width,
      height,
      maxX,
      maxY,
      left: (stageSize - width) / 2 + safePan.x,
      top: (stageSize - height) / 2 + safePan.y,
      safePan,
    };
  }, [natural, pan, stageSize, zoom]);

  useEffect(() => {
    if (pan.x !== geometry.safePan.x || pan.y !== geometry.safePan.y) setPan(geometry.safePan);
  }, [geometry.safePan.x, geometry.safePan.y, pan.x, pan.y]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, pan: geometry.safePan };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setPan({
      x: clamp(drag.pan.x + event.clientX - drag.x, -geometry.maxX, geometry.maxX),
      y: clamp(drag.pan.y + event.clientY - drag.y, -geometry.maxY, geometry.maxY),
    });
  };

  const stopDragging = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  };

  const crop = async () => {
    const image = imageRef.current;
    if (!file || !image || !image.complete || !natural.width || !natural.height) {
      message.warning('图片仍在加载，请稍后再试');
      return;
    }
    setProcessing(true);
    try {
      const sourceX = clamp(-geometry.left / geometry.scale, 0, natural.width);
      const sourceY = clamp(-geometry.top / geometry.scale, 0, natural.height);
      const sourceSize = Math.min(stageSize / geometry.scale, natural.width - sourceX, natural.height - sourceY);
      const attempts = [
        { size: 512, quality: 0.84 },
        { size: 512, quality: 0.72 },
        { size: 448, quality: 0.66 },
        { size: 384, quality: 0.60 },
        { size: 320, quality: 0.54 },
        { size: 256, quality: 0.46 },
        { size: 224, quality: 0.38 },
      ];
      let blob: Blob | null = null;
      for (const attempt of attempts) {
        const canvas = document.createElement('canvas');
        canvas.width = attempt.size;
        canvas.height = attempt.size;
        const context = canvas.getContext('2d', { alpha: true });
        if (!context) throw new Error('浏览器不支持图片裁剪');
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, attempt.size, attempt.size);
        blob = await canvasToBlob(canvas, attempt.quality);
        if (blob.size <= TARGET_BYTES) break;
      }
      if (!blob) throw new Error('头像裁剪失败');
      if (blob.size > TARGET_BYTES) throw new Error('头像压缩后仍然过大，请换一张图片再试');
      const baseName = file.name.replace(/\.[^.]+$/, '').slice(0, 60) || 'avatar';
      await onConfirm(new File([blob], `${baseName}-avatar.webp`, { type: 'image/webp', lastModified: Date.now() }));
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  return <Modal
    open={open}
    title="裁剪头像（1:1）"
    okText="裁剪并上传"
    cancelText="取消"
    confirmLoading={processing}
    maskClosable={false}
    onOk={crop}
    onCancel={onCancel}
    width={460}
  >
    <Typography.Paragraph type="secondary">拖动图片选择方形区域，使用滑块缩放。上传前会压缩为 WebP，通常不超过 256KB。</Typography.Paragraph>
    <div
      ref={stageRef}
      className="avatar-crop-stage"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
    >
      {objectUrl ? <img
        ref={imageRef}
        src={objectUrl}
        alt="头像裁剪预览"
        draggable={false}
        onLoad={event => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
        style={{ width: geometry.width, height: geometry.height, left: geometry.left, top: geometry.top }}
      /> : null}
      <div className="avatar-crop-grid" />
    </div>
    <div className="avatar-crop-zoom"><span>缩放</span><Slider min={1} max={3} step={0.01} value={zoom} onChange={setZoom} /></div>
  </Modal>;
}
